import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MoreHorizontal, Trash2, CheckCircle, XCircle, Key, Shield, ShieldCheck, Eye, EyeOff, ArrowLeft, Users, UserX, UserCheck, Mail, ShieldAlert } from 'lucide-react';

const roleLabels = {
  admin: { label: 'Admin', className: 'bg-[#0F172A] text-white border-[#0F172A]' },
  quality_manager: { label: 'Quality Manager', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  viewer: { label: 'Viewer', className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const userStatusLabels = {
  active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending: { label: 'Pending Approval', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-200' },
  suspended: { label: 'Suspended', className: 'bg-slate-200 text-slate-600 border-slate-300' },
};

const appRoleLabels = {
  manager: { label: 'Manager', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  app_user: { label: 'App User', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  viewer: { label: 'Viewer', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  none: { label: 'No Access', className: 'bg-red-50 text-red-400 border-red-100' },
};

const ERP_APP_LABELS = {
  calibration_cms: "Calibration Management",
  supplier_mgmt: "Supplier Management",
  inventory_mgmt: "Inventory Management",
  dispatch_mgmt: "Dispatch Management",
  hr_training: "HR & Training",
  purchase: "Purchase",
  tooling_mgmt: "Tooling Management",
  production: "Production",
  contract_mgmt: "Contract Management",
  project_mgmt: "Program Management"
};

const ERP_APPS = Object.keys(ERP_APP_LABELS);

export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user: currentUser } = useAuth();
  
  // Password reset modal state
  const [resetPasswordModal, setResetPasswordModal] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  
  // App roles modal state
  const [appRolesModal, setAppRolesModal] = useState({ open: false, user: null });
  const [editingAppRoles, setEditingAppRoles] = useState({});
  const [appRolesLoading, setAppRolesLoading] = useState(false);
  
  // Contact email modal state
  const [contactEmailModal, setContactEmailModal] = useState({ open: false, user: null });
  const [contactEmail, setContactEmail] = useState('');
  const [designation, setDesignation] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [contactEmailLoading, setContactEmailLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await usersAPI.list();
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersAPI.updateRole(userId, newRole);
      toast.success('Role updated');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleApprove = async (userId, userName) => {
    try {
      await usersAPI.updateStatus(userId, 'approve');
      toast.success(`${userName} approved`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleReject = async (userId, userName) => {
    if (!window.confirm(`Reject user "${userName}"?`)) return;
    try {
      await usersAPI.updateStatus(userId, 'reject');
      toast.success(`${userName} rejected`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject user');
    }
  };

  const handleSuspend = async (userId, userName) => {
    if (!window.confirm(`Suspend user "${userName}"? They will not be able to log in.`)) return;
    try {
      await usersAPI.updateStatus(userId, 'suspend');
      toast.success(`${userName} suspended`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleActivate = async (userId, userName) => {
    try {
      await usersAPI.updateStatus(userId, 'activate');
      toast.success(`${userName} activated`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate user');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This cannot be undone.`)) return;
    try {
      await usersAPI.delete(userId);
      toast.success('User deleted');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Contact email handlers
  const openContactEmailModal = (user) => {
    setContactEmailModal({ open: true, user });
    setContactEmail(user.contact_email || '');
    setDesignation(user.designation || '');
    setOtpEmail(user.otp_email || '');
  };

  const handleUpdateContactEmail = async () => {
    const trimmedOtp = otpEmail.trim();
    if (trimmedOtp && !trimmedOtp.toLowerCase().endsWith('@bhpl.co.in')) {
      toast.error('OTP email must be a @bhpl.co.in address');
      return;
    }
    setContactEmailLoading(true);
    try {
      const calls = [
        usersAPI.updateContactEmail(contactEmailModal.user.id, contactEmail),
        usersAPI.updateDesignation(contactEmailModal.user.id, designation),
      ];
      // Only touch OTP email if the admin actually changed it.
      if (trimmedOtp !== (contactEmailModal.user.otp_email || '')) {
        calls.push(usersAPI.updateOtpEmail(contactEmailModal.user.id, trimmedOtp));
      }
      await Promise.all(calls);
      toast.success(`Profile updated for ${contactEmailModal.user.name}`);
      setContactEmailModal({ open: false, user: null });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setContactEmailLoading(false);
    }
  };

  // Password reset handlers
  const openResetPasswordModal = (user) => {
    setResetPasswordModal({ open: true, user });
    setNewPassword('');
    setShowNewPassword(false);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetLoading(true);
    try {
      await usersAPI.resetPassword(resetPasswordModal.user.id, newPassword);
      toast.success(`Password reset for ${resetPasswordModal.user.name}`);
      setResetPasswordModal({ open: false, user: null });
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  // App roles handlers
  const openAppRolesModal = (user) => {
    const currentRoles = user.app_roles || {};
    // Initialize with current roles or 'none' for all apps
    const initialRoles = {};
    ERP_APPS.forEach(app => {
      initialRoles[app] = currentRoles[app] || 'none';
    });
    setEditingAppRoles(initialRoles);
    setAppRolesModal({ open: true, user });
  };

  const handleAppRoleChange = (appKey, role) => {
    setEditingAppRoles(prev => ({ ...prev, [appKey]: role }));
  };

  const handleSaveAppRoles = async () => {
    setAppRolesLoading(true);
    try {
      await usersAPI.updateAppRoles(appRolesModal.user.id, editingAppRoles);
      toast.success(`App roles updated for ${appRolesModal.user.name}`);
      setAppRolesModal({ open: false, user: null });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update app roles');
    } finally {
      setAppRolesLoading(false);
    }
  };

  const pendingUsers = users.filter(u => u.status === 'pending');
  const otherUsers = users.filter(u => u.status !== 'pending');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> ERP Home
            </Button>
            <div className="h-4 w-px bg-slate-300" />
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              <h1 className="text-lg font-semibold text-[#0F172A]">User Management</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div data-testid="users-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">User Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">{users.length} registered user{users.length !== 1 ? 's' : ''}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/login-audit')}
          className="gap-1.5 border-slate-300"
          data-testid="open-login-audit-btn"
        >
          <ShieldAlert className="w-3.5 h-3.5 text-red-600" /> Login Audit
        </Button>
      </div>

      {/* Pending Approvals */}
      {pendingUsers.length > 0 && (
        <div className="space-y-2" data-testid="pending-users-section">
          <h2 className="text-sm font-semibold text-amber-700 flex items-center gap-2">
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            Pending Approvals ({pendingUsers.length})
          </h2>
          <div className="border border-amber-200 rounded-md overflow-hidden bg-amber-50/30">
            <Table>
              <TableHeader>
                <TableRow className="bg-amber-50/50">
                  <TableHead className="text-xs font-semibold text-slate-500">Name</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500">Email</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500">Registered</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 w-[180px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUsers.map(u => (
                  <TableRow key={u.id} data-testid={`pending-user-${u.id}`}>
                    <TableCell className="text-sm font-medium text-[#0F172A]">{u.name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-400">{u.created_at?.substring(0, 10)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(u.id, u.name)}
                          className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`approve-user-${u.id}`}
                        >
                          <CheckCircle className="w-3 h-3" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(u.id, u.name)}
                          className="h-7 text-xs gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`reject-user-${u.id}`}
                        >
                          <XCircle className="w-3 h-3" /> Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* All Users */}
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs font-semibold text-slate-500">Name</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500">Email</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500">Global Role</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500">Status</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500">Registered</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-sm text-slate-400">Loading...</TableCell>
              </TableRow>
            ) : otherUsers.map(u => {
              const role = roleLabels[u.role] || roleLabels.viewer;
              const uStatus = userStatusLabels[u.status] || userStatusLabels.active;
              const isSelf = u.id === currentUser?.id;
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="text-sm font-medium text-[#0F172A]">
                    {u.name} {isSelf && <span className="text-[10px] text-slate-400 ml-1">(you)</span>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-500">{u.email}</div>
                    {u.contact_email && u.contact_email !== u.email && (
                      <div className="flex items-center gap-1 text-[10px] text-blue-600">
                        <Mail className="w-2.5 h-2.5" />
                        {u.contact_email}
                      </div>
                    )}
                    {u.otp_email && (
                      <div className="flex items-center gap-1 text-[10px] text-emerald-600" data-testid={`otp-email-tag-${u.id}`}>
                        <ShieldCheck className="w-2.5 h-2.5" />
                        OTP: {u.otp_email}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {isSelf ? (
                      <Badge variant="outline" className={`text-[10px] ${role.className}`}>{role.label}</Badge>
                    ) : (
                      <Select value={u.role} onValueChange={(val) => handleRoleChange(u.id, val)}>
                        <SelectTrigger className="h-7 w-40 text-xs" data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="quality_manager">Quality Manager</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${uStatus.className}`}>{uStatus.label}</Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono text-slate-400">{u.created_at?.substring(0, 10)}</TableCell>
                  <TableCell>
                    {!isSelf && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`user-actions-${u.id}`}>
                            <MoreHorizontal className="w-4 h-4 text-slate-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem
                            onClick={() => openResetPasswordModal(u)}
                            className="text-xs"
                            data-testid={`reset-password-${u.id}`}
                          >
                            <Key className="w-3 h-3 mr-2" /> Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => openContactEmailModal(u)}
                            className="text-xs"
                            data-testid={`contact-email-${u.id}`}
                          >
                            <Mail className="w-3 h-3 mr-2" /> Profile & OTP Email
                          </DropdownMenuItem>
                          {u.role !== 'admin' && (
                            <DropdownMenuItem
                              onClick={() => openAppRolesModal(u)}
                              className="text-xs"
                              data-testid={`app-roles-${u.id}`}
                            >
                              <Shield className="w-3 h-3 mr-2" /> Manage App Roles
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {u.status === 'active' ? (
                            <DropdownMenuItem
                              onClick={() => handleSuspend(u.id, u.name)}
                              className="text-xs text-amber-600"
                              data-testid={`suspend-user-${u.id}`}
                            >
                              <UserX className="w-3 h-3 mr-2" /> Suspend User
                            </DropdownMenuItem>
                          ) : u.status === 'suspended' ? (
                            <DropdownMenuItem
                              onClick={() => handleActivate(u.id, u.name)}
                              className="text-xs text-emerald-600"
                              data-testid={`activate-user-${u.id}`}
                            >
                              <UserCheck className="w-3 h-3 mr-2" /> Activate User
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem
                            onClick={() => handleDelete(u.id, u.name)}
                            className="text-xs text-red-600"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-2" /> Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Reset Password Modal */}
      <Dialog open={resetPasswordModal.open} onOpenChange={(open) => !open && setResetPasswordModal({ open: false, user: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Reset Password</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Set a new password for <strong>{resetPasswordModal.user?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-xs font-medium">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-slate-400">Share this password securely with the user. They can change it after logging in.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetPasswordModal({ open: false, user: null })}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetLoading || !newPassword || newPassword.length < 6}
              className="text-xs bg-[#0F172A] hover:bg-[#1E293B]"
              data-testid="confirm-reset-password"
            >
              {resetLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* App Roles Modal */}
      <Dialog open={appRolesModal.open} onOpenChange={(open) => !open && setAppRolesModal({ open: false, user: null })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg">Manage App Roles</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Configure ERP app access for <strong>{appRolesModal.user?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4 max-h-[400px] overflow-y-auto">
            {ERP_APPS.map(appKey => {
              const currentRole = editingAppRoles[appKey] || 'none';
              const roleStyle = appRoleLabels[currentRole] || appRoleLabels.none;
              return (
                <div key={appKey} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-md">
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{ERP_APP_LABELS[appKey]}</p>
                    <p className="text-[10px] text-slate-400">{appKey}</p>
                  </div>
                  <Select value={currentRole} onValueChange={(val) => handleAppRoleChange(appKey, val)}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`app-role-${appKey}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manager">
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-blue-500 rounded-full" /> Manager
                        </span>
                      </SelectItem>
                      <SelectItem value="app_user">
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-emerald-500 rounded-full" /> App User
                        </span>
                      </SelectItem>
                      <SelectItem value="viewer">
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-slate-400 rounded-full" /> Viewer
                        </span>
                      </SelectItem>
                      <SelectItem value="none">
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-red-300 rounded-full" /> No Access
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAppRolesModal({ open: false, user: null })}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAppRoles}
              disabled={appRolesLoading}
              className="text-xs bg-[#0F172A] hover:bg-[#1E293B]"
              data-testid="save-app-roles"
            >
              {appRolesLoading ? 'Saving...' : 'Save Roles'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Email Modal */}
      <Dialog open={contactEmailModal.open} onOpenChange={(open) => !open && setContactEmailModal({ open: false, user: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              User Profile Settings
            </DialogTitle>
            <DialogDescription>
              Update notification email and designation for <strong>{contactEmailModal.user?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="login-email" className="text-xs text-slate-500">Login Email (User ID)</Label>
              <Input
                id="login-email"
                value={contactEmailModal.user?.email || ''}
                disabled
                className="h-9 text-sm bg-slate-50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email" className="text-xs text-slate-500">Contact Email (for notifications)</Label>
              <Input
                id="contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="Enter contact email or leave empty to use login email"
                className="h-9 text-sm"
                data-testid="contact-email-input"
              />
              <p className="text-[10px] text-slate-400">Leave empty to send notifications to login email</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-designation" className="text-xs text-slate-500">Designation / Title</Label>
              <Input
                id="user-designation"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                placeholder="e.g. Purchase Manager, Quality Engineer"
                className="h-9 text-sm"
                data-testid="user-designation-input"
              />
              <p className="text-[10px] text-slate-400">Shown in outgoing email sign-offs (e.g. &quot;Regards, Anita Kulkarni, Purchase Manager — BHPL&quot;).</p>
            </div>
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <Label htmlFor="otp-email" className="text-xs text-slate-500 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Login OTP Email (2FA)
              </Label>
              <Input
                id="otp-email"
                type="email"
                value={otpEmail}
                onChange={(e) => setOtpEmail(e.target.value)}
                placeholder="name@bhpl.co.in"
                className="h-9 text-sm"
                data-testid="otp-email-input"
              />
              <p className="text-[10px] text-slate-400">
                Where this user's login verification code is sent. Must be a <strong>@bhpl.co.in</strong> address.
                Setting it lets the user skip email confirmation. Leave empty to make the user confirm their own email at next login.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setContactEmailModal({ open: false, user: null })}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateContactEmail}
              disabled={contactEmailLoading}
              className="text-xs bg-blue-600 hover:bg-blue-700"
              data-testid="save-contact-email"
            >
              {contactEmailLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </div>
      </main>
    </div>
  );
}
